import type { ChangeEvent } from 'react';

import { useDecisions } from '@libs/common-next';
import {
    AUTOSHIP_ONE_TIME_OPTION,
    AUTOSHIP_ONE_TIME_SELECTION_VALUE,
    type AutoshipFrequency,
    type AutoshipUnitOfMeasure,
    getAutoshipFrequencyOptions,
} from '@libs/common-types';
import { SelectField } from '@libs/shared-ui';

import { useProduct } from '@/entities/product';
import {
    type AutoshipFrequencySelection,
    saveCustomerFrequencySelection,
    setValue,
    usePurchaseFormWatch,
} from '@/entities/purchase-form';

import { MOST_COMMON_LABEL_SUFFIX } from './autoshipOptions';
import { getMostCommonOptionValue } from './mapRecommendedAutoshipFrequency';

type SelectFrequencyProps = {
    withOneTime: boolean;
    mostCommonValue?: string;
};

export const SelectFrequency = ({ withOneTime, mostCommonValue }: SelectFrequencyProps) => {
    const isEnabled = useDecisions('dyn_as_freq');
    const as_freq_5_7 = useDecisions('as_freq_5_7');
    const { product } = useProduct();
    const frequency = usePurchaseFormWatch('autoshipFrequency');
    const unit = usePurchaseFormWatch('autoshipUnit');

    const recommendedMostCommonOptionValue = getMostCommonOptionValue({
        isEnabled,
        recommendedFrequency: product?.recommendedAutoshipFrequency,
        recommendedUnit: product?.recommendedAutoshipFrequencyUnitOfMeasure,
        as_freq_5_7,
    });
    const mostCommonOptionValue = mostCommonValue ?? recommendedMostCommonOptionValue;

    const recurringFrequencyItems = getAutoshipFrequencyOptions(as_freq_5_7).map(item => ({
        ...item,
        label: item.value === mostCommonOptionValue ? `${item.label}${MOST_COMMON_LABEL_SUFFIX}` : item.label,
    }));
    const frequencyItems = withOneTime
        ? [
              {
                  label: AUTOSHIP_ONE_TIME_OPTION.label,
                  value: AUTOSHIP_ONE_TIME_SELECTION_VALUE,
              },
              ...recurringFrequencyItems,
          ]
        : recurringFrequencyItems;

    const persistSelection = (selection: AutoshipFrequencySelection) => {
        if (isEnabled && product?.sku) {
            saveCustomerFrequencySelection(product.sku, selection);
        }
        setValue('autoshipFrequency', selection.autoshipFrequency);
        setValue('autoshipUnit', selection.autoshipUnit);
    };

    const handleSelect = (event: ChangeEvent<HTMLSelectElement>) => {
        const { value } = event.target;

        if (value === AUTOSHIP_ONE_TIME_SELECTION_VALUE) {
            persistSelection({ autoshipFrequency: 'one_time', autoshipUnit: 'one_time' });
            return;
        }

        const [nextFreq, nextUnit] = value.split('_') as [AutoshipFrequency, AutoshipUnitOfMeasure];
        persistSelection({ autoshipFrequency: nextFreq, autoshipUnit: nextUnit });
    };

    return (
        <SelectField
            label="Ships every"
            items={frequencyItems}
            onChange={handleSelect}
            data-testid="select-frequency"
            value={`${frequency}_${unit}`}
        />
    );
};
